import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { formatPriceStringV2 } from 'client/site-modules/shared/utils/price-utils';
import { EventToolbox } from 'client/utils/event-toolbox';
import { TrackingConstant } from 'client/tracking/constant';
import { PaymentRatioInsightsText } from 'site-modules/lease-deals/components/payment-ratio-insights/payment-ratio-insights-text';
import { KeyInsightsList } from 'site-modules/lease-deals/components/key-insights-list/key-insights-list';
import { EDM_LEASE_DEAL_INSIGHTS_CREATIVE_ID } from 'site-modules/lease-deals/constants/lease-deals-constants';
import { PriceComparisonBar } from 'site-modules/lease-deals/components/price-comparison-bar/price-comparison-bar';
import { getLeaseDealsValueRating } from 'client/site-modules/lease-deals/utils/get-lease-deals-value-rating';

import './deal-insights.scss';

const RATING_GRADIENT_CLASS_MAP = {
  1: 'rating-gradient-poor',
  2: 'rating-gradient-below-average',
  3: 'rating-gradient-typical',
  4: 'rating-gradient-good',
  5: 'rating-gradient-excellent',
};

export function DealInsights({
  effectivePaymentRatio,
  effectivePayment,
  dueAtSigning,
  effectivePaymentMedian = undefined,
  bodyType,
  term,
  lifetimeCost = undefined,
  monthlyPayment,
  msrp,
  isShop4931ZeroDownRoute = false,
}) {
  const { rating } = getLeaseDealsValueRating(effectivePaymentRatio);

  useEffect(() => {
    EventToolbox.fireTrackAction({
      event_type: TrackingConstant.EVENT_TYPE_WIDGET_VIEW,
      event_data: {
        action_name: TrackingConstant.ACTION_WIDGET_VIEW,
        action_cause: TrackingConstant.ACTION_CAUSE_SCROLL,
        creative_id: EDM_LEASE_DEAL_INSIGHTS_CREATIVE_ID,
      },
    });
  }, []);

  return (
    <div
      data-tracking-parent={EDM_LEASE_DEAL_INSIGHTS_CREATIVE_ID}
      className={classNames('deal-insights rounded-24 p-1_5', RATING_GRADIENT_CLASS_MAP[rating])}
    >
      <h3 className="fw-medium mb-1 size-16 text-cool-gray-10">Our take</h3>
      <PaymentRatioInsightsText effectivePaymentRatio={effectivePaymentRatio} className="text-center text-md-start" />
      <hr className="my-1_5 border-cool-gray-80" />
      <h3 className="fw-medium mb-1 size-16 text-cool-gray-10">Real cost</h3>
      <div className="row g-0 align-items-md-center mb-1">
        <div className="col-12 col-md-6 text-center text-md-start pb-1_5 pb-md-0">
          <div className="size-30 fw-bold text-cool-gray-10 mb-0_5">{formatPriceStringV2(effectivePayment)}/mo</div>
          <p className="m-0 text-cool-gray-40 size-16">
            {isShop4931ZeroDownRoute
              ? 'with $0 down payment included'
              : `with ${formatPriceStringV2(dueAtSigning)} due at signing included`}
          </p>
        </div>
        <div className="col-12 col-md-6 pt-md-1">
          <PriceComparisonBar
            effectivePayment={effectivePayment}
            effectivePaymentRatio={effectivePaymentRatio}
            effectivePaymentMedian={effectivePaymentMedian}
          />
        </div>
      </div>

      <KeyInsightsList
        effectivePayment={effectivePayment}
        effectivePaymentMedian={effectivePaymentMedian}
        term={term}
        bodyType={bodyType}
        lifetimeCost={lifetimeCost}
        dueAtSigning={dueAtSigning}
        monthlyPayment={monthlyPayment}
        msrp={msrp}
        effectivePaymentRatio={effectivePaymentRatio}
        isShop4931ZeroDownRoute={isShop4931ZeroDownRoute}
      />
    </div>
  );
}

DealInsights.propTypes = {
  effectivePaymentRatio: PropTypes.number.isRequired,
  effectivePayment: PropTypes.number.isRequired,
  effectivePaymentMedian: PropTypes.number,
  dueAtSigning: PropTypes.number.isRequired,
  bodyType: PropTypes.string.isRequired,
  term: PropTypes.number.isRequired,
  lifetimeCost: PropTypes.number,
  monthlyPayment: PropTypes.number.isRequired,
  msrp: PropTypes.number.isRequired,
  isShop4931ZeroDownRoute: PropTypes.bool,
};
